import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { api, fmt } from "../lib/api";
import { toast } from "sonner";

// European roulette wheel order (0..36 clockwise, ball-track ordering)
const WHEEL_ORDER = [0,32,15,19,4,21,2,25,17,34,6,27,13,36,11,30,8,23,10,5,24,16,33,1,20,14,31,9,22,18,29,7,28,12,35,3,26];
const RED_NUMS = new Set([1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36]);
const CHIP_VALUES_HUF = [500, 1000, 5000, 10000, 50000];
const CHIP_VALUES_EUR = [1, 5, 10, 25, 100];
const CHIP_COLORS = ["#a40606","#2d6a4f","#1a4480","#1a1a1a","#d4af37"];

function colorOf(n){ if(n===0) return "green"; return RED_NUMS.has(n)?"red":"black"; }

function WheelSVG({ resultNumber, spinning }) {
  const SEGMENTS = WHEEL_ORDER.length; // 37
  const anglePer = 360 / SEGMENTS;
  // Compute rotation for landing on resultNumber (segment index from wheel_order[0] on top)
  return (
    <div className="roulette-wheel-outer">
      <div className={`roulette-wheel ${spinning ? "spinning-inner" : ""}`}>
        <svg viewBox="0 0 200 200" className="w-full h-full">
          {WHEEL_ORDER.map((n, i) => {
            const startAngle = i * anglePer - 90 - anglePer/2;
            const endAngle = startAngle + anglePer;
            const c = colorOf(n);
            const fill = c === "red" ? "#a40606" : c === "black" ? "#0a0a0a" : "#0b4a26";
            const largeArc = anglePer > 180 ? 1 : 0;
            const startRad = (startAngle * Math.PI) / 180;
            const endRad = (endAngle * Math.PI) / 180;
            const x1 = 100 + 96 * Math.cos(startRad);
            const y1 = 100 + 96 * Math.sin(startRad);
            const x2 = 100 + 96 * Math.cos(endRad);
            const y2 = 100 + 96 * Math.sin(endRad);
            const midAngle = (startAngle + endAngle) / 2;
            const midRad = (midAngle * Math.PI) / 180;
            const tx = 100 + 72 * Math.cos(midRad);
            const ty = 100 + 72 * Math.sin(midRad);
            return (
              <g key={i}>
                <path d={`M 100 100 L ${x1} ${y1} A 96 96 0 ${largeArc} 1 ${x2} ${y2} Z`}
                      fill={fill} stroke="#d4af37" strokeWidth="0.4"/>
                <text x={tx} y={ty} fill="#fff" fontSize="7" fontFamily="Cinzel"
                      textAnchor="middle" dominantBaseline="middle"
                      transform={`rotate(${midAngle + 90} ${tx} ${ty})`}>
                  {n}
                </text>
              </g>
            );
          })}
          {/* Inner ring */}
          <circle cx="100" cy="100" r="46" fill="none" stroke="#d4af37" strokeWidth="0.8"/>
        </svg>
      </div>
      <div className="roulette-hub">HAVANA</div>
      {/* Ball */}
      <div className={`roulette-ball ${spinning ? "spinning" : ""}`}
           style={ !spinning && resultNumber != null ? (() => {
             const idx = WHEEL_ORDER.indexOf(resultNumber);
             const angle = idx * anglePer;
             const rad = ((angle - 90) * Math.PI) / 180;
             const r = 100; // pocket radius (px, half of wheel)
             const cx = 150; // wheel center in outer 300px box
             const cy = 150;
             return {
               top: `${cy + r * Math.sin(rad) - 7}px`,
               left: `${cx + r * Math.cos(rad) - 7}px`,
               transform: "none",
               transition: "none",
             };
           })() : {} }/>
      {/* Marker on top */}
      <div style={{
        position: "absolute", top: -6, left: "50%", transform: "translateX(-50%)",
        width: 0, height: 0, borderLeft: "10px solid transparent", borderRight: "10px solid transparent",
        borderTop: "16px solid #d4af37", filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.6))",
      }}/>
    </div>
  );
}

export default function Roulette() {
  const { currency, lang, updateBalance } = useApp();
  const [chip, setChip] = useState(currency==="HUF"?500:1);
  const [bets, setBets] = useState({});
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const chips = currency==="HUF"?CHIP_VALUES_HUF:CHIP_VALUES_EUR;

  const addBet = (key) => {
    if (spinning) return;
    setBets(prev => ({ ...prev, [key]: (prev[key]||0) + chip }));
  };
  const clearBets = () => setBets({});
  const total = Object.values(bets).reduce((a,b)=>a+b,0);

  const parseKey = (key) => {
    const [type, val] = key.split(":");
    if (type==="num") return { type:"number", value: Number(val), amount: bets[key] };
    if (type==="color") return { type: val, amount: bets[key] };
    if (type==="parity") return { type: val, amount: bets[key] };
    if (type==="range") return { type: val, amount: bets[key] };
    if (type==="dozen") return { type:"dozen", value: Number(val), amount: bets[key] };
    if (type==="column") return { type:"column", value: Number(val), amount: bets[key] };
    return null;
  };

  const spin = async () => {
    if (total<=0) return toast.error("Place at least one bet");
    setSpinning(true); setResult(null);
    try {
      const payload = Object.keys(bets).map(parseKey).filter(Boolean);
      const r = await api.post("/games/roulette/spin", { bets: payload, currency });
      setTimeout(() => {
        setResult(r.data);
        setHistory(h => [{ number: r.data.number, color: colorOf(r.data.number) }, ...h].slice(0, 15));
        updateBalance(currency, r.data.balance);
        if (r.data.payout > 0) toast.success(`+${fmt(r.data.payout, currency)}`);
        else toast.error(t(lang,"bj.lose"));
        setSpinning(false);
        setBets({});
      }, 2400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
      setSpinning(false);
    }
  };

  const Chip = ({ k }) => bets[k] ? (
    <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-[#d4af37] text-black text-[10px] flex items-center justify-center font-bold shadow-lg border-2 border-white chip-place-anim">{bets[k]>=1000?`${(bets[k]/1000).toFixed(1)}K`:bets[k]}</div>
  ) : null;

  const NumCell = ({ n }) => {
    const c = colorOf(n);
    const k = `num:${n}`;
    const isWin = result && result.number === n;
    return (
      <button data-testid={`roulette-num-${n}`} onClick={()=>addBet(k)}
              className={`relative h-12 w-full ${c==="red"?"bg-[#a40606]":c==="black"?"bg-black":"bg-[#0b4a26]"} border border-white/25 hover:opacity-90 text-white font-serif ${isWin?"ring-2 ring-[#d4af37] ring-inset":""}`}>
        {n}<Chip k={k}/>
      </button>
    );
  };

  const grid = [];
  for (let row=0; row<3; row++){
    const cols = [];
    for (let col=1; col<=12; col++){
      const n = col*3 - row;
      cols.push(n);
    }
    grid.push(cols);
  }

  return (
    <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-4xl gold-text">{t(lang,"roulette.title")}</h1>
        {/* Recent numbers ticker */}
        <div className="flex items-center gap-1.5 overflow-x-auto">
          <span className="text-[10px] uppercase tracking-widest text-white/40 pr-2">History</span>
          {history.slice(0, 15).map((h, i) => (
            <span key={i} className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
              h.color === "red" ? "bg-[#a40606] border-white/20 text-white" :
              h.color === "black" ? "bg-black border-white/20 text-white" :
              "bg-[#0b4a26] border-[#d4af37] text-white"
            }`}>{h.number}</span>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[340px_1fr] gap-8">
        {/* Wheel + croupier */}
        <div className="flex flex-col items-center">
          <div className="dealer-avatar mb-4" style={{width: 70, height: 70, fontSize: 34}}>🎩</div>
          <div className="text-[9px] uppercase tracking-[0.4em] text-white/50 mb-2">Croupier · Marco</div>

          <WheelSVG resultNumber={result?.number} spinning={spinning}/>

          {result && !spinning && (
            <div className={`mt-4 result-marquee ${
              result.color === "red" ? "bg-[#a40606]/40 text-red-200" :
              result.color === "black" ? "bg-black/60 text-white" :
              "bg-[#0b4a26]/60 text-emerald-200"
            }`} data-testid="roulette-result">
              {result.number} · {(result.color || colorOf(result.number)).toUpperCase()}
            </div>
          )}

          <div className="text-xs uppercase tracking-widest text-white/40 mb-2 mt-6">{t(lang,"roulette.select_chip")}</div>
          <div className="flex gap-2 flex-wrap justify-center mb-4">
            {chips.map((v,i)=>(
              <button key={v} data-testid={`roulette-chip-${v}`} onClick={()=>setChip(v)}
                className={`chip ${chip===v?"selected":""}`} style={{color:CHIP_COLORS[i%CHIP_COLORS.length]}}>{v>=1000?`${v/1000}K`:v}</button>
            ))}
          </div>
          <div className="text-sm text-white/60 mb-3">Total: <span className="text-[#d4af37] font-serif" data-testid="roulette-total">{fmt(total, currency)}</span></div>
          <button data-testid="roulette-spin" onClick={spin} disabled={spinning||total<=0} className="btn-gold w-full py-4 rounded-md">{spinning?"SPINNING...":t(lang,"roulette.spin")}</button>
          <button data-testid="roulette-clear" onClick={clearBets} disabled={spinning} className="btn-ghost w-full py-3 rounded-md mt-2">{t(lang,"roulette.clear")}</button>
        </div>

        {/* Board */}
        <div className="felt-table-pro p-6 relative">
          <div className="table-logo-watermark" style={{top: "50%", fontSize: "2rem"}}>
            HAVANA
            <span className="sub" style={{fontSize: "0.65rem"}}>ROULETTE · EUROPEAN</span>
          </div>
          <div className="relative z-10">
            <div className="flex gap-1">
              <button data-testid="roulette-num-0" onClick={()=>addBet("num:0")}
                      className={`relative w-14 bg-[#0b4a26] border border-white/25 text-white font-serif text-xl flex items-center justify-center ${result?.number===0?"ring-2 ring-[#d4af37] ring-inset":""}`}>
                0<Chip k="num:0"/>
              </button>
              <div className="flex-1 grid grid-rows-3 gap-1">
                {grid.map((row,ri)=>(
                  <div key={ri} className="grid grid-cols-12 gap-1">
                    {row.map(n => <NumCell key={n} n={n}/>)}
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-1">
                {[1,2,3].map(col=>(
                  <button key={col} data-testid={`roulette-column-${col}`} onClick={()=>addBet(`column:${col}`)}
                          className="relative h-12 px-3 bg-black/40 border border-white/25 text-white text-xs uppercase tracking-widest">
                    2:1<Chip k={`column:${col}`}/>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1 mt-2 ml-[60px]">
              {[1,2,3].map(d=>(
                <button key={d} data-testid={`roulette-dozen-${d}`} onClick={()=>addBet(`dozen:${d}`)}
                        className="relative h-10 bg-black/40 border border-white/25 text-white text-xs uppercase tracking-widest">
                  {d===1?"1st 12":d===2?"2nd 12":"3rd 12"}<Chip k={`dozen:${d}`}/>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-6 gap-1 mt-2 ml-[60px]">
              <button data-testid="roulette-low" onClick={()=>addBet("range:low")} className="relative h-10 bg-black/40 border border-white/25 text-xs uppercase text-white">{t(lang,"roulette.low")}<Chip k="range:low"/></button>
              <button data-testid="roulette-even" onClick={()=>addBet("parity:even")} className="relative h-10 bg-black/40 border border-white/25 text-xs uppercase text-white">{t(lang,"roulette.even")}<Chip k="parity:even"/></button>
              <button data-testid="roulette-red" onClick={()=>addBet("color:red")} className="relative h-10 bg-[#a40606] border border-white/25 text-xs uppercase text-white">{t(lang,"roulette.red")}<Chip k="color:red"/></button>
              <button data-testid="roulette-black" onClick={()=>addBet("color:black")} className="relative h-10 bg-black border border-white/25 text-xs uppercase text-white">{t(lang,"roulette.black")}<Chip k="color:black"/></button>
              <button data-testid="roulette-odd" onClick={()=>addBet("parity:odd")} className="relative h-10 bg-black/40 border border-white/25 text-xs uppercase text-white">{t(lang,"roulette.odd")}<Chip k="parity:odd"/></button>
              <button data-testid="roulette-high" onClick={()=>addBet("range:high")} className="relative h-10 bg-black/40 border border-white/25 text-xs uppercase text-white">{t(lang,"roulette.high")}<Chip k="range:high"/></button>
            </div>

            {/* Payout info banner */}
            <div className="mt-4 flex flex-wrap gap-2 justify-center text-[9px] uppercase tracking-widest text-white/50">
              <span className="result-marquee">Straight 35:1</span>
              <span className="result-marquee">Split 17:1</span>
              <span className="result-marquee">Column/Dozen 2:1</span>
              <span className="result-marquee">Red/Black 1:1</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
